// Lo que hace Sócrates con la boca cuando contestas: un «mmm», un «uuh», un «ajá».
//
// DE DÓNDE SALE. Gabri, 6 de octubre de 2026, viendo el juego hecho y mudo: *«haz un sonido más
// fuerte, con animación intensa… que haya algún sonido de persona sintetizado que diga ¡Muy
// bien! o una onomatopeya tipo uuh, no»*. El juego tenía razón en no puntuar y estaba muerto
// igualmente: diez preguntas y nada que devolviera nada.
//
// Y NO DICE «MUY BIEN». Eso fue lo único que se discutió y lo decidió él: este juego no tiene
// aciertos —no hay respuesta correcta en un dilema moral, y ésa es la frase con la que Sócrates
// cierra—, así que un «¡muy bien!» enseñaría justo lo contrario de lo que el juego entero
// intenta enseñar, y encima con la autoridad de una máquina. **Lo que suena no es una nota: es
// que te han oído.**
//
// LOS TRES GESTOS NO SE REPARTEN POR ACIERTO, SINO POR LO QUE HACES. Intervenir y no intervenir
// son dos cosas distintas y suenan distinto porque son distintas, no porque una esté mejor.
// Quien escucha los diez seguidos no puede sacar de los sonidos qué «había que» contestar: lo
// único que puede sacar es cuántas veces metió la mano.
//
// ── Y AHORA LA PARTE QUE COSTÓ UNA VUELTA ──────────────────────────────────────────────────
//
// LA PRIMERA VERSIÓN NO SE OÍA. Gabri: *«está la música del mundo 3D de fondo, no se escucha
// ninguna voz ni reacción»*, y antes de eso: *«solo tiembla un poco en rojo el dibujo y la
// opción escogida, nada más»* — o sea, el código estaba puesto y corriendo, y el sonido salía
// del altavoz sin llegar a nadie.
//
// LA CAUSA, Y ES UNA REGLA PARA TODO LO QUE SUENE EN ESTA CASA: **un grave que el altavoz del
// aula no reproduce es un sonido que no existe.** Para que se distinguiera de Alpha la bajé una
// octava entera, a 146-262 Hz, que sobre el papel es «la voz de un anciano» y en la práctica es
// la banda donde el altavoz de un portátil ya no da casi nada. Encima iba con la ganancia de
// antes: a 146 Hz hace falta bastante más energía que a 880 para que suene IGUAL de alto, y yo
// había puesto la misma. Dos errores en la misma dirección.
//
// LO GRAVE ES RELATIVO, NO ABSOLUTO. Lo que hace que esto no suene a Alpha no es estar en el
// sótano del espectro: es estar POR DEBAJO de ella —ella va de 494 a 988— con otro timbre y
// otro ritmo. Con el re dórico de su propia lira, sin bajar octava, se distingue a la primera y
// además se oye en el Chromebook del aula, que es donde se juega.
//
// Y CADA SÍLABA LLEVA UN ARMÓNICO, que es lo que la separa de un pitido: una voz tiene cuerpo,
// y una sola onda triangular suena a timbre de microondas. El armónico va flojo —un tercio— y
// por encima: da presencia sin subir el volumen, que es exactamente lo que necesita un altavoz
// pequeño.
//
// EN RE DÓRICO, que es su sitio: las mismas notas de su lira, «el modo que los griegos llamaban
// serio».
//
// Lógica pura, sin audio y sin React: devuelve sílabas y las toca `lib/voces.js`, el mismo
// reproductor que todo lo demás. Un segundo motor de audio acabaría con dos sonando a la vez el
// día que alguien conteste deprisa, que es lo que esta casa ya decidió con la música.
//
// LO PRUEBAN LAS REGLAS DE LA VOZ EN `scripts/prueba_tranvia.mjs`, y no un portero propio: lo
// que hay que proteger aquí no son las frecuencias —si un «uuh» suena bien se decide oyéndolo—,
// es que **ningún gesto reparta aprobados** y que **todo caiga donde un altavoz lo reproduce**,
// y lo primero sólo se puede medir contra el juego que los usa.

import { DESTELLOS_DE_LA_PLAZA } from "./pentatonicaDeLaPlaza.mjs";

/** Su escala: re dórico, el de su lira. */
export const NOTAS = { re: 293.66, fa: 349.23, sol: 392.0, la: 440.0, do: 523.25 };

/**
 * LO MÁS GRAVE QUE PUEDE SONAR ALGO EN ESTA CASA, en hercios.
 *
 * No es una preferencia: es el suelo de lo que reproduce un altavoz de portátil o de
 * Chromebook, que es donde se juega esto. Por debajo de aquí el cono no mueve aire y da igual
 * la ganancia que se le ponga — lo único que se consigue es creer que suena porque en unos
 * cascos buenos sí se oye.
 */
export const SE_OYE_DESDE = 200;

/**
 * Y LO MÁS AGUDO QUE PUEDE PONERSE SIN PISARLE LA VOZ A ALPHA.
 *
 * No es un número: es **la nota más grave de ella**, leída de su escala. Escrito a mano (lo
 * estuvo, como 494) falla por doce centésimas el día que alguien mire bien, y falla del todo el
 * día que Alpha cambie de escala y esto siga diciendo que está por debajo. Lo que hay que
 * mantener es la relación, así que la relación es lo que se escribe.
 */
export const DEBAJO_DE_ALPHA = Math.min(...DESTELLOS_DE_LA_PLAZA);

/** Lo más larga que puede ser una reacción. Pasado esto deja de ser una interjección y es una
 *  frase, y una frase aquí sería Sócrates contestando por ti. */
export const TOPE = 0.75;

/** Lo flojo que va el armónico que le da cuerpo, respecto a la fundamental. */
export const CUERPO = 0.34;

/**
 * LOS GESTOS, y cada uno dice lo que dice por su forma y no por su letra:
 *
 *  · `golpe`  — «uuh». Has metido la mano: algo ha pasado. Cae y se queda abajo, que es lo que
 *               hace una voz cuando encaja un golpe. **No es un «mal»**: es un «ha pasado».
 *  · `piensa` — «mmm». No has intervenido. Casi plano y largo: el sonido de alguien que no
 *               tiene nada que objetar y tampoco nada que celebrar.
 *  · `apunta` — «ajá». Lo deja anotado y sigue. Dos sílabas, la segunda sube.
 *
 * NINGUNO ES MÁS FUERTE QUE OTRO, y eso está medido en el portero: el día que a uno se le suba
 * el volumen, el juego empieza a tener una respuesta preferida sin que nadie lo haya escrito.
 */
export const GESTOS = {
  golpe: [
    { hz: NOTAS.la, hasta: NOTAS.fa, cuando: 0, dura: 0.12, fuerza: 0.2 },
    { hz: NOTAS.fa, hasta: NOTAS.re, cuando: 0.12, dura: 0.3, fuerza: 0.19 },
  ],
  piensa: [
    { hz: NOTAS.re, hasta: NOTAS.re * 1.03, cuando: 0, dura: 0.2, fuerza: 0.185 },
    { hz: NOTAS.re * 1.03, hasta: NOTAS.fa, cuando: 0.21, dura: 0.24, fuerza: 0.2 },
  ],
  apunta: [
    { hz: NOTAS.fa, hasta: NOTAS.fa, cuando: 0, dura: 0.1, fuerza: 0.18 },
    // SUBE HASTA CASI EL DO Y NO HASTA EL DO: el do de su escala cae en 523 y Alpha empieza en
    // 494, así que rematar ahí sería hablar en su banda. Un seis por ciento basta para que se
    // oiga como una pregunta y se queda por debajo de ella.
    { hz: NOTAS.la, hasta: NOTAS.la * 1.06, cuando: 0.12, dura: 0.22, fuerza: 0.2 },
  ],
};

export const CUALES = Object.keys(GESTOS);

/**
 * Las sílabas de un gesto, **con su armónico ya puesto**, o una lista vacía si ese gesto no
 * existe: un nombre mal escrito deja a Sócrates callado, nunca revienta la partida a mitad.
 *
 * El armónico sale de aquí y no del que toca, por lo mismo que las notas: quien reproduce no
 * tiene que saber cómo se construye una voz, sólo soltar lo que se le dé.
 */
export function gesto(cual) {
  const suyo = GESTOS[cual];
  if (!suyo) return [];
  return suyo.flatMap((s) => [
    s,
    { ...s, hz: s.hz * 2, hasta: s.hasta * 2, fuerza: s.fuerza * CUERPO },
  ]);
}

/** Lo que dura, en segundos. Lo mira el portero contra `TOPE` y lo usa quien anima. */
export function duracion(cual) {
  const s = GESTOS[cual] || [];
  return s.length ? Math.max(...s.map((x) => x.cuando + x.dura)) : 0;
}

/**
 * QUÉ HACE SÓCRATES CUANDO CONTESTAS, y es aquí donde se ve que no juzga: lo único que mira es
 * **si has intervenido**, que es un hecho, y no si lo que hiciste salva a más gente, que sería
 * una opinión suya metida en un sonido.
 *
 * `pasar` no es ni una cosa ni la otra: no has dicho nada, así que sólo lo apunta.
 */
export function alResponder(intervino) {
  if (intervino === true) return "golpe";
  if (intervino === false) return "piensa";
  return "apunta";
}

export default gesto;
